import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CompassIcon, FolderIcon, GlobeIcon, MessageSquareIcon, ShieldIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { PatternDecor } from '../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const parentPaths = [{
  icon: UsersIcon,
  title: 'عضوية دعم الوالدين',
  description: 'توفر عضوية الوالدين إرشادًا مستمرًا وموارد ودعمًا من المختصين للوالدين المسلمين الذين يربّون أبناءهم في مجتمعات غير مسلمة.',
  action: 'استكشف العضوية',
  to: '/ar/parents/membership',
  tone: 'sage' as const
}, {
  icon: GlobeIcon,
  title: 'فهم البيئة التي ينمو فيها طفلك',
  description: 'تربية الأطفال المسلمين في الدول غير المسلمة تتطلب وعيًا واستعدادًا وقوة عاطفية.',
  action: 'اعرف المزيد',
  to: '/ar/parents/parenting-in-non-muslim-countries',
  tone: 'sky' as const
}, {
  icon: ShieldIcon,
  title: 'الحماية والوعي والاستجابة الهادئة',
  description: 'غالبًا ما يكون التمييز خفيًا وصعبًا على الأطفال شرحه. ينصب التركيز على الوقاية والكرامة والحل، لا المواجهة.',
  action: 'اعرف المزيد',
  to: '/ar/parents/preventing-discrimination',
  tone: 'rose' as const
}, {
  icon: FolderIcon,
  title: 'دعم عملي للتربية اليومية',
  description: 'يحصل الوالدان على موارد منتقاة بعناية، صُممت للأسر المسلمة التي تعيش في بيئات غير مسلمة.',
  action: 'عرض الموارد',
  to: '/ar/parents/resources-guidance',
  tone: 'sand' as const
}];
const membershipIncludes = ['جلسات شهرية مع مختصين', 'نقاشات خاصة بالوالدين', 'أدلة وموارد عملية', 'دعم وتحديثات ذات أولوية'];
const membershipFor = ['الاستجابة بهدوء للمواقف الصعبة في المدرسة', 'حماية الهوية دون عزلة', 'التواصل بثقة مع المؤسسات', 'دعم الأطفال عند طرح الأسئلة الحساسة'];
const steps = [{
  icon: CompassIcon,
  title: 'اختر الموضوع',
  description: 'ابدأ بالعضوية أو إرشادات التربية أو الوقاية من التمييز أو الموارد العملية، وفق ما تحتاجه أسرتك الآن.',
  tone: 'sage' as const
}, {
  icon: MessageSquareIcon,
  title: 'احصل على دعم موجّه',
  description: 'انضم إلى الجلسات، واستكشف المواد، وتعلّم استجابات هادئة وعملية لمواقف الحياة الواقعية.',
  tone: 'sky' as const
}, {
  icon: CheckCircle2Icon,
  title: 'طبّقه في الحياة اليومية',
  description: 'استخدم أدوات تراعي الثقافة، وترتكز إلى الإيمان، وغير سياسية، ويسهل تطبيقها في الحياة اليومية.',
  tone: 'rose' as const
}];
const resourcePrinciples = ['مراعية للاختلافات الثقافية', 'غير سياسية', 'سهلة التطبيق في الحياة اليومية', 'آمنة للأطفال والوالدين', 'تركز على الكرامة والاندماج'];
export function ArabicParents() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2718" data-mp="3lf2kw90cqkqp">
      <section className="relative isolate overflow-hidden" data-id="element-2719" data-mp="19uuymh43a6l8">
        <PatternDecor side="left" y="middle" size="lg" data-id="element-2720" data-mp="14up48t6fq5zg" />
        <GeometricAccent variant="dots" className="absolute left-8 top-8 h-32 w-32" opacity={0.4} data-id="element-2721" data-mp="14t5nz2ks3uxo" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-2722" data-mp="10tncexeukw7i">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-2723" data-mp="vusnddqaebey">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-2724" data-mp="1sk9wy3se4hlx">
              <span className="text-sm font-semibold text-forest-700" data-id="element-2725" data-mp="1okstth0frwjx">ندعم الوالدين. نحمي الأطفال. نقوّي الأسر.</span>
              <h1 className="mt-3 font-display text-5xl font-semibold leading-[1.35] text-ink-900 md:text-7xl" data-id="element-2726" data-mp="1eswi335fg7x8">الآباء والأمهات</h1>
              <div className="mt-6 max-w-xl space-y-3 leading-loose text-ink-700" data-id="element-2727" data-mp="2c8iqah5o6tn0">
                <p data-id="element-2728" data-mp="27htz72xwjyoe">يلعب الوالدان دورًا محوريًا في كيفية نمو الأطفال المسلمين واندماجهم وشعورهم بالأمان في الدول غير المسلمة.</p>
                <p data-id="element-2729" data-mp="1r0zwjl1zbn8w">صُممت برامجنا للوالدين من أجل <strong className="text-ink-900" data-id="element-2730" data-mp="1r0qa27g57y7y">الدعم والإرشاد والتمكين</strong>، ومنحهم الوضوح والثقة والأدوات العملية لتحديات الحياة الواقعية.</p>
                <p data-id="element-2731" data-mp="1i1wiivho5y19">هذا ليس نصائح نظرية في التربية، بل <strong className="text-ink-900" data-id="element-2732" data-mp="1i2xs0vj1ecgv">دعم موجّه يستند إلى المبادئ الإسلامية والخبرة المعاشة.</strong></p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-2733" data-mp="2aq0m9bgok11b">
                <Link to="/ar/parents/membership" data-id="element-2734" data-mp="2aqk6wzxpt3j1"><Button variant="primary" size="lg" data-id="element-2735" data-mp="2asehplf9vf9e">استكشف العضوية</Button></Link>
                <Link to="/ar/parents/resources-guidance" data-id="element-2736" data-mp="2etq8vvdso8jp"><Button variant="secondary" size="lg" data-id="element-2737" data-mp="210qtruhxcgco">عرض الموارد</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2738" data-mp="25ya9kyu9t16q">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2739" data-mp="2ygehtbyh4i0n"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/eG2z3gWLjme6SMefkcRt94/Parents6.jpg" alt="أسرة مسلمة مع طفلين تقف معًا في المنزل" data-id="element-2740" data-mp="37tn0qoe5wevp" /></div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-sage-50/60 py-16 md:py-24" data-id="element-2741" data-mp="3hjgb5m2kinyd">
        <PatternDecor side="right" y="top" size="lg" data-id="element-2742" data-mp="3clol6xlhhbwt" />
        <Container data-id="element-2743" data-mp="2ovasr5yzlb1p">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2744" data-mp="2owls8tlefwn0">اختر المسار المناسب لك</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2745" data-mp="2kvs4c07yihu4">{parentPaths.map((path, index) => <PathCard key={path.title} path={path} index={index} data-id="element-2746" data-mp="2yuje5afsdhcg" data-mpi={index} />)}</div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2747" data-mp="2tt2h5q0faf15">
        <Container data-id="element-2748" data-mp="e2zs0lwqi1y5">
          <div className="mx-auto max-w-2xl text-center" data-id="element-2749" data-mp="e2g9p4369bjl">
            <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-2750" data-mp="94ereldklg96">منظومة دعم، لا دورة تدريبية</h2>
            <p className="mt-5 leading-loose text-ink-700" data-id="element-2751" data-mp="h5vw9apy6lgk">تساعد العضوية الوالدين على فهم التحديات التي يواجهها أطفالهم، والاستجابة بهدوء وثقة، وحماية قيم الأسرة، وتجنب العزلة والحيرة.</p>
            <p className="mt-4 font-semibold text-ink-900" data-id="element-2752" data-mp="q5afpq7p5vrk">هذه منظومة دعم وليست دورة تدريبية.</p>
            <div className="mt-8 flex justify-center" data-id="element-2753" data-mp="v5f0hsfn1cnl"><Link to="/ar/waitlist" data-id="element-2754" data-mp="v4dg2xcqfdt7"><Button variant="primary" data-id="element-2755" data-mp="v2j3q7fj0u0q">انضم إلى العضوية</Button></Link></div>
            <p className="mt-4 text-sm text-ink-500" data-id="element-2756" data-mp="3to3qjv93c6i1">محتوى جديد شهريًا • التسجيلات متاحة • يمكنك الإلغاء في أي وقت</p>
          </div>
          <div className="mt-12 grid items-start gap-5 md:grid-cols-2" data-id="element-2757" data-mp="7faxnna9xgxn">
            <ChecklistCard title="يشملها الاشتراك" points={membershipIncludes} data-id="element-2758" data-mp="7dzv1qskef84" />
            <ChecklistCard title="مثالية للوالدين الذين يرغبون في" points={membershipFor} data-id="element-2759" data-mp="c6swo5sql7fz" />
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-2760" data-mp="3fv4fq6k970gt">
        <PatternDecor side="left" y="bottom" size="md" data-id="element-2761" data-mp="3fsro11op1bqk" />
        <Container data-id="element-2762" data-mp="38aabsnpfnitw">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2763" data-mp="38atqzwnp7t48">كيف يعمل الدعم</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2764" data-mp="33antw0ynteer">{steps.map((step, index) => <StepCard key={step.title} step={step} index={index} data-id="element-2765" data-mp="2ybkscqhw1qbj" data-mpi={index} />)}</div>
        </Container>
      </section>

      <section className="bg-sand-50/60 py-16 md:py-24" data-id="element-2766" data-mp="2tgf4vn8u83lc">
        <Container data-id="element-2767" data-mp="2pdsl89mbloz8">
          <div className="grid gap-10 lg:grid-cols-2" data-id="element-2768" data-mp="2pead6u1y9h1c">
            <div data-id="element-2769" data-mp="2klhb6dxv8msy">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-2770" data-mp="2kkzgvmurkino">موارد يمكنك الوثوق بها</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-2771" data-mp="2fn7dnwoo5a46">يحصل الوالدان على أدلة عملية، ومحاضرات مسجلة لمختصين، وأدوات للحوار الأسري، وقوائم تحقق، صُممت خصيصًا للأسر المسلمة في البيئات غير المسلمة.</p>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-2772" data-mp="25qulx4cop6c2">كُتب كل شيء لتقليل التوتر وزيادة الوضوح ومساعدتك على الاستجابة بثقة دون تصعيد أو مواجهة.</p>
            </div>
            <div data-id="element-2773" data-mp="3pl5ux0xwzsd9">
              <div className="rounded-2xl border border-sage-200 bg-white p-6 shadow-soft md:p-7" data-id="element-2774" data-mp="3pkma9cgvqpvj">
                <p className="mb-4 text-sm font-semibold text-forest-700" data-id="element-2775" data-mp="3ui6079mxwr3b">كل المواد مصممة لتكون:</p>
                <ul className="space-y-3 text-ink-700" data-id="element-2776" data-mp="3flf00y5fzbcn">{resourcePrinciples.map(principle => <li key={principle} className="flex items-center gap-3" data-id="element-2777" data-mp="3kjgfza7kmujw"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-forest-700" aria-hidden="true" data-id="element-2778" data-mp="36tcxmubtbkzq" /><span data-id="element-2779" data-mp="36ricbewmpqv4">{principle}</span></li>)}</ul>
              </div>
              <p className="mt-4 text-sm leading-loose text-ink-500" data-id="element-2780" data-mp="3blml83pgg8yu">نقدّم التثقيف والإرشاد، ولا نقدّم تمثيلًا قانونيًا.</p>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2781" data-mp="31v1o6kpfjci1">
        <Container data-id="element-2782" data-mp="2nur17sr8qi25">
          <div className="grid items-center gap-10 lg:grid-cols-2" data-id="element-2783" data-mp="2nu8ws8ufcxj5">
            <div data-id="element-2784" data-mp="2rw2ovsqk0t7p"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2785" data-mp="2rvufiatn4619">لست مضطرًا لمعرفة كل شيء وحدك</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-2786" data-mp="2970gitm9e20b">احصل على إرشاد مستمر وأدوات عملية ودعم هادئ، صُممت للأسر المسلمة التي تربي أطفالها في بيئات غير مسلمة.</p></div>
            <div className="flex flex-wrap gap-3 lg:justify-center" data-id="element-2787" data-mp="2i88bb1pkgkpn"><Link to="/ar/waitlist" data-id="element-2788" data-mp="2ia1ici43d6y6"><Button variant="primary" data-id="element-2789" data-mp="2i63x9qxye5bn">انضم إلى العضوية</Button></Link><Link to="/ar/parents/resources-guidance" data-id="element-2790" data-mp="2d8c9n94cd5d9"><Button variant="secondary" data-id="element-2791" data-mp="2dbq9a9lmf0h0">تصفّح الموارد</Button></Link></div>
          </div>
        </Container>
      </section>
    </div>;
}
function PathCard({
  path,
  index
}: {
  path: (typeof parentPaths)[number];
  index: number;
}) {
  const Icon = path.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`flex flex-col gap-4 rounded-2xl border p-6 shadow-soft md:p-7 ${toneClasses[path.tone]}`} data-id="element-2792" data-mp="30xoe5voq4p98"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/15 bg-white text-forest-700" data-id="element-2793" data-mp="35yu8fxy18kkv"><Icon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-2794" data-mp="2td2oyk8c2jlz" /></span><h3 className="font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-2795" data-mp="2okj8nfi9hadh">{path.title}</h3><p className="text-[15px] leading-loose text-ink-700" data-id="element-2796" data-mp="2kioa2irptb3e">{path.description}</p><Link to={path.to} className="mt-auto pt-1 text-sm font-medium text-forest-700" data-id="element-2797" data-mp="2fk37wdswkhcb">{path.action} <span aria-hidden="true" data-id="element-2798" data-mp="2fia02v6jzr2q">←</span></Link></motion.article>;
}
function ChecklistCard({
  title,
  points
}: {
  title: string;
  points: string[];
}) {
  return <article className="rounded-2xl border border-sage-200 bg-white p-6 shadow-soft md:p-7" data-id="element-2799" data-mp="389toqulx5xc4"><h3 className="text-sm font-semibold text-forest-700" data-id="element-2800" data-mp="338w5sft34z9k">{title}</h3><ul className="mt-5 space-y-3 text-ink-700" data-id="element-2801" data-mp="2yakxi9ifuwtg">{points.map(point => <li key={point} className="flex items-center gap-3" data-id="element-2802" data-mp="2ya2qchu8my6r"><CheckCircle2Icon size={16} className="shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-2803" data-mp="1ru8t3qp0wipf" /><span data-id="element-2804" data-mp="1muwbbkd0e1tp">{point}</span></li>)}</ul></article>;
}
function StepCard({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const Icon = step.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[step.tone]}`} data-id="element-2805" data-mp="1cpeanuspojuz"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-2806" data-mp="1rhge1otzpuzr"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2807" data-mp="1vhqlz71cevh9" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2808" data-mp="20ga40wctfn4c">{step.title}</h3><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-2809" data-mp="25fwh95sn93d5">{step.description}</p></motion.article>;
}